// Add this update to StockOverview.js - This shows how to incorporate the screener tags
// You'll need to add this to the existing StockOverview component

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ValueScore from './ValueScore';
import AddToWatchlist from './AddToWatchlist';

const StockOverview = ({ data, onPeerClick, onScreenerClick }) => {
  const [timeframe, setTimeframe] = useState('1year');

  const { stock } = data;

  if (!stock) return <div>No stock data available</div>;
  
  // Debug what we're getting
  console.log("Stock data received:", stock);
  
  // Check for screener tags
  console.log("Checking for screener tags:", data.screeners);
  
  // Helper functions to check data availability
  const hasIncomeData = data.income && 
                      data.income.incomeStatements && 
                      data.income.incomeStatements.length > 0;
                      
  const hasPeersData = data.peers && 
                      data.peers.peers && 
                      data.peers.peers.length > 0;
                      
  console.log("Has peers:", hasPeersData);
  
  // Helper function to ensure a value is a number
  const ensureNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
      console.log("Converting null/undefined to default:", value, "→", defaultValue);
      return defaultValue;
    }
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) {
      console.log("Converting NaN to default:", value, "→", defaultValue);
      return defaultValue;
    }
    return num;
  };
  
  // Format large numbers for better readability
  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Ensure we're working with a number
    const num = ensureNumber(value);
    
    // For numbers less than 1 million
    if (Math.abs(num) < 1000000) {
      return new Intl.NumberFormat('en-US').format(num);
    }
    
    // For millions
    if (Math.abs(num) < 1000000000) {
      return new Intl.NumberFormat('en-US', { 
        maximumFractionDigits: 2 
      }).format(num / 1000000) + 'M';
    }
    
    // For billions
    return new Intl.NumberFormat('en-US', { 
      maximumFractionDigits: 2 
    }).format(num / 1000000000) + 'B';
  };
  
  // Safe formatting function that never throws
  const safeFormat = (value, formatter = (x) => x, defaultValue = 'N/A') => {
    try {
      if (value === null || value === undefined) return defaultValue;
      return formatter(value);
    } catch (e) {
      console.error("Error formatting value:", value, e);
      return defaultValue;
    }
  };
  
  // Helper function to safely format a number with fixed decimal places
  const safeToFixed = (value, decimals = 2) => {
    try {
      const num = ensureNumber(value);
      return num.toFixed(decimals);
    } catch (e) {
      console.error("Error formatting number:", e);
      return '0.00';
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return 'N/A';
    }
  };
  
  // Format screener name for display
  const formatScreenerName = (screenerSlug) => {
    // Convert slug to title case with spaces
    return screenerSlug.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Calculate growth percentages based on financial data
  const calculateGrowth = () => {
    console.log("DEBUG GROWTH: Starting calculateGrowth function");
    console.log("DEBUG GROWTH: Income data:", data.income);
    
    if (!data.income || !data.income.incomeStatements || data.income.incomeStatements.length < 2) {
      console.log("DEBUG GROWTH: Not enough income data to calculate growth metrics");
      return null;
    }
    
    const statements = data.income.incomeStatements;
    console.log(`DEBUG GROWTH: Found ${statements.length} income statements`);
    
    // Log each statement to help with debugging
    statements.forEach((statement, index) => {
      console.log(`DEBUG GROWTH: Statement ${index} - date: ${statement.date}, revenue: ${statement.revenue}, net_income: ${statement.net_income}, eps: ${statement.eps}`);
    });
    
    // Sort statements by date (newest first)
    const sortedStatements = [...statements].sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });
    
    console.log("DEBUG GROWTH: Sorted statements (newest first):", 
      sortedStatements.map(s => `${s.date} - Revenue: ${s.revenue}`));
    
    // Find statements for different years
    const currentYear = sortedStatements[0];
    const twoYearPrior = sortedStatements.find(s => {
      const statementDate = new Date(s.date);
      const currentDate = new Date(currentYear.date);
      return currentDate.getFullYear() - statementDate.getFullYear() >= 2;
    });
    const threeYearPrior = sortedStatements.find(s => {
      const statementDate = new Date(s.date);
      const currentDate = new Date(currentYear.date);
      return currentDate.getFullYear() - statementDate.getFullYear() >= 3;
    });
    const fiveYearPrior = sortedStatements.find(s => {
      const statementDate = new Date(s.date);
      const currentDate = new Date(currentYear.date);
      return currentDate.getFullYear() - statementDate.getFullYear() >= 5;
    });
    
    // Calculate growth percentages
    const calculateGrowthPercent = (current, prior) => {
      if (!prior || !current) return null;
      const currentValue = ensureNumber(current);
      const priorValue = ensureNumber(prior);
      if (priorValue === 0) return null;
      return ((currentValue - priorValue) / Math.abs(priorValue)) * 100;
    };
    
    return {
      latestYear: new Date(currentYear.date).getFullYear(),
      latest: {
        revenue: currentYear.revenue,
        netIncome: currentYear.net_income,
        eps: currentYear.eps
      },
      revenue: {
        twoYear: twoYearPrior ? calculateGrowthPercent(currentYear.revenue, twoYearPrior.revenue) : null,
        threeYear: threeYearPrior ? calculateGrowthPercent(currentYear.revenue, threeYearPrior.revenue) : null,
        fiveYear: fiveYearPrior ? calculateGrowthPercent(currentYear.revenue, fiveYearPrior.revenue) : null
      },
      netIncome: {
        twoYear: twoYearPrior ? calculateGrowthPercent(currentYear.net_income, twoYearPrior.net_income) : null,
        threeYear: threeYearPrior ? calculateGrowthPercent(currentYear.net_income, threeYearPrior.net_income) : null,
        fiveYear: fiveYearPrior ? calculateGrowthPercent(currentYear.net_income, fiveYearPrior.net_income) : null
      },
      eps: {
        twoYear: twoYearPrior ? calculateGrowthPercent(currentYear.eps, twoYearPrior.eps) : null,
        threeYear: threeYearPrior ? calculateGrowthPercent(currentYear.eps, threeYearPrior.eps) : null,
        fiveYear: fiveYearPrior ? calculateGrowthPercent(currentYear.eps, fiveYearPrior.eps) : null
      }
    };
  };
  
  const growthData = calculateGrowth();

  // ── Price chart ────────────────────────────────────────────────────────────
  const getPriceChartData = () => {
    if (!data.historical || !data.historical.prices) return null;
    const maxEntries = { '1month': 22, '3months': 65, '6months': 130, '1year': 252 };
    const sliced = data.historical.prices.slice(0, maxEntries[timeframe] || 252);
    return sliced.map(p => ({
      date: p.date,
      close: parseFloat(p.close) || null,
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getPriceStats = (chartData) => {
    if (!chartData || chartData.length === 0) return null;
    const closes = chartData.map(p => p.close).filter(v => v !== null);
    if (!closes.length) return null;
    const high = Math.max(...closes);
    const low  = Math.min(...closes);
    const latest   = closes[closes.length - 1];
    const earliest = closes[0];
    const change = latest - earliest;
    return { high, low, change, percentChange: (change / earliest) * 100 };
  };

  const chartData  = getPriceChartData();
  const priceStats = getPriceStats(chartData);

  return (
    <div className="stock-overview">
      <div className="stock-header">
        <div className="stock-title-container">
          <div className="stock-title-row">
            <h2>{safeFormat(stock.name)} ({safeFormat(stock.symbol)})</h2>
            <AddToWatchlist symbol={stock.symbol} />
          </div>
          
          {/* Add Screener Tags Section */}
          {data.inScreeners && data.screeners && data.screeners.length > 0 && (
            <div className="screener-tags">
              {data.screeners.map((screener, index) => (
                <span 
                  key={index} 
                  className="screener-tag"
                  onClick={() => onScreenerClick && onScreenerClick(screener)}
                  title={`View ${formatScreenerName(screener)} screener`}
                >
                  {formatScreenerName(screener)}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="stock-price-container">
          <div className="stock-price">
            ${safeFormat(stock.price, (price) => ensureNumber(price).toFixed(2), '0.00')}
          </div>
          {(() => {
            const price = ensureNumber(stock.price);
            const pct = ensureNumber(stock.change_percent);
            const dollarChange = pct !== 0 ? price * pct / (100 + pct) : 0;
            const sign = dollarChange >= 0 ? '+' : '';
            return (
              <div className={`stock-change ${pct >= 0 ? 'positive' : 'negative'}`}>
                {sign}${Math.abs(dollarChange).toFixed(2)} ({sign}{pct.toFixed(2)}%)
              </div>
            );
          })()}
        </div>
      </div>

      <ValueScore symbol={stock.symbol} />

      {chartData && chartData.length > 0 && (
        <div className="price-chart">
          <div className="price-chart-header">
            <h3>Price History</h3>
            <div className="timeframe-selector">
              {['1month','3months','6months','1year'].map(tf => (
                <button
                  key={tf}
                  className={timeframe === tf ? 'active' : ''}
                  onClick={() => setTimeframe(tf)}
                >
                  {tf === '1month' ? '1M' : tf === '3months' ? '3M' : tf === '6months' ? '6M' : '1Y'}
                </button>
              ))}
            </div>
          </div>

          {priceStats && (
            <div className="price-stats">
              <div className="stat-item">
                <span className="stat-label">High</span>
                <span className="stat-value">${priceStats.high.toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Low</span>
                <span className="stat-value">${priceStats.low.toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Change</span>
                <span className={`stat-value ${priceStats.change >= 0 ? 'positive' : 'negative'}`}>
                  {priceStats.change >= 0 ? '+' : ''}{priceStats.change.toFixed(2)} ({priceStats.percentChange.toFixed(1)}%)
                </span>
              </div>
            </div>
          )}

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                tickFormatter={v => {
                  const d = new Date(v);
                  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                }}
                tick={{ fontSize: 11 }}
                minTickGap={40}
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={v => `$${v}`}
                tick={{ fontSize: 11 }}
                width={60}
              />
              <Tooltip
                formatter={v => [`$${Number(v).toFixed(2)}`, 'Close']}
                labelFormatter={l => new Date(l).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              />
              <Line type="monotone" dataKey="close" stroke="#8884d8" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="stock-details">
        <div className="detail-row">
          <div className="detail-item">
            <span className="detail-label">Market Cap</span>
            <span className="detail-value">${safeFormat(stock.market_cap, formatNumber, 'N/A')}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Volume</span>
            <span className="detail-value">{safeFormat(stock.volume, formatNumber, 'N/A')}</span>
          </div>
        </div>
        
        <div className="detail-row">
          <div className="detail-item">
            <span className="detail-label">Last Updated</span>
            <span className="detail-value">{safeFormat(stock.fetched_at, formatDate, 'N/A')}</span>
          </div>
        </div>
      </div>
      
      {/* Growth Metrics Section with loading state handling */}
      {growthData ? (
        <div className="growth-metrics">
          <h3>Growth Metrics</h3>
          <table className="growth-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>{growthData.latestYear}</th>
                <th>2-Year Growth</th>
                <th>3-Year Growth</th>
                <th>5-Year Growth</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="gm-label">Revenue</td>
                <td className="gm-value">
                  {growthData.latest.revenue !== null && growthData.latest.revenue !== undefined ? '$' + formatNumber(growthData.latest.revenue) : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.revenue.twoYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.revenue.twoYear !== null ? safeToFixed(growthData.revenue.twoYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.revenue.threeYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.revenue.threeYear !== null ? safeToFixed(growthData.revenue.threeYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.revenue.fiveYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.revenue.fiveYear !== null ? safeToFixed(growthData.revenue.fiveYear) + '%' : 'N/A'}
                </td>
              </tr>
              <tr>
                <td className="gm-label">Net Income</td>
                <td className="gm-value">
                  {growthData.latest.netIncome !== null && growthData.latest.netIncome !== undefined ? '$' + formatNumber(growthData.latest.netIncome) : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.netIncome.twoYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.netIncome.twoYear !== null ? safeToFixed(growthData.netIncome.twoYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.netIncome.threeYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.netIncome.threeYear !== null ? safeToFixed(growthData.netIncome.threeYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.netIncome.fiveYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.netIncome.fiveYear !== null ? safeToFixed(growthData.netIncome.fiveYear) + '%' : 'N/A'}
                </td>
              </tr>
              <tr>
                <td className="gm-label">Earnings Per Share</td>
                <td className="gm-value">
                  {growthData.latest.eps !== null && growthData.latest.eps !== undefined ? '$' + safeToFixed(ensureNumber(growthData.latest.eps)) : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.eps.twoYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.eps.twoYear !== null ? safeToFixed(growthData.eps.twoYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.eps.threeYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.eps.threeYear !== null ? safeToFixed(growthData.eps.threeYear) + '%' : 'N/A'}
                </td>
                <td className={`gm-value ${growthData.eps.fiveYear >= 0 ? 'gm-positive' : 'gm-negative'}`}>
                  {growthData.eps.fiveYear !== null ? safeToFixed(growthData.eps.fiveYear) + '%' : 'N/A'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : hasIncomeData ? null : (
        <div className="growth-metrics">
          <h3>Growth Metrics</h3>
          <p>Financial data is being fetched. This may take a moment...</p>
          <div className="loading-progress-indicator"></div>
        </div>
      )}
      
      {data.ratios && data.ratios.financialRatios && data.ratios.financialRatios.length > 0 && (
        <div className="key-ratios">
          <h3>Key Ratios</h3>
          <div className="ratios-grid">
            <div className="ratio-item">
              <span className="ratio-label">P/E Ratio</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].pe_ratio, 
                  (pe) => ensureNumber(pe).toFixed(2), 'N/A')}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">P/S Ratio</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].price_to_sales, 
                  (ps) => ensureNumber(ps).toFixed(2), 'N/A')}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">P/B Ratio</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].price_to_book, 
                  (pb) => ensureNumber(pb).toFixed(2), 'N/A')}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">Debt/Equity</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].debt_to_equity, 
                  (de) => ensureNumber(de).toFixed(2), 'N/A')}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">ROE</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].return_on_equity, 
                  (roe) => (ensureNumber(roe) * 100).toFixed(2) + '%', 'N/A')}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">Profit Margin</span>
              <span className="ratio-value">
                {safeFormat(data.ratios.financialRatios[0].net_profit_margin, 
                  (npm) => (ensureNumber(npm) * 100).toFixed(2) + '%', 'N/A')}
              </span>
            </div>
            {data.dividend && (
              <div className="ratio-item">
                <span className="ratio-label">Dividend Yield</span>
                <span className="ratio-value">
                  {safeFormat(data.dividend.dividendYieldPercentage, 
                    (yield_) => yield_, 
                    safeFormat(data.dividend.dividendYield, 
                      (y) => (ensureNumber(y) * 100).toFixed(2) + '%', 'N/A'))}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Peers Section with enhanced empty state handling */}
      {data.peers && (
        <div className="stock-peers">
          <h3>Peer Companies</h3>
          
          {hasPeersData ? (
            <div className="peers-grid">
              {console.log("DEBUG PEERS: Rendering peers section with data:", data.peers)}
              {Array.isArray(data.peers.peers) && data.peers.peers.length > 0 ? (
                // If peers is nested under a 'peers' property and not empty
                data.peers.peers.map((peer, index) => (
                  <div 
                    key={index} 
                    className="peer-item" 
                    onClick={() => onPeerClick && onPeerClick(peer.symbol)}
                    title={`Compare ${stock.symbol} with ${peer.symbol}`}
                  >
                    <div className="peer-symbol">{peer.symbol}</div>
                    <div className="peer-name">{peer.name}</div>
                    {peer.price && (
                      <div className="peer-price">${ensureNumber(peer.price).toFixed(2)}</div>
                    )}
                  </div>
                ))
              ) : Array.isArray(data.peers) && data.peers.length > 0 ? (
                // If peers is a direct array and not empty
                data.peers.map((peer, index) => (
                  <div 
                    key={index} 
                    className="peer-item" 
                    onClick={() => onPeerClick && onPeerClick(peer.symbol)}
                    title={`Compare ${stock.symbol} with ${peer.symbol}`}
                  >
                    <div className="peer-symbol">{peer.symbol}</div>
                    <div className="peer-name">{peer.name}</div>
                    {peer.price && (
                      <div className="peer-price">${ensureNumber(peer.price).toFixed(2)}</div>
                    )}
                  </div>
                ))
              ) : (
                // No peers data found
                <div className="no-peers-message">
                  No peer companies found for this stock.
                </div>
              )}
            </div>
          ) : (
            // If peers data is pending or being fetched
            <div className="no-peers-message">
              No peer companies data available.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockOverview;
